'use client';

import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import type { Brand } from '@/domainLogic/index.ts';
import { Text, Placeholder, DeprecatedLinkButton as LinkButton } from '@lmn/design-system';
import Stars from '@/app/common/components/Stars';
import { ReviewsSummaryEvo } from '@/app/components/hotel-detail/ReviewsSummary';
import { DebugReviewsInfo } from '@/app/components/hotel-detail/DebugReviewsInfo';
import { useFetchHotelStatic, useIsHotelStaticLoading } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useMetaReview } from '@/app/components/hotel-detail/hooks/useMetaReview';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { hasReviewsContent, isTripAdvisorSource, isTrustYouSource } from '@/app/common/reviews/reviewSource';
import { getCheckinCheckoutDates, URL_DATE_FORMAT } from '@/app/components/hotel-detail/common/dates';
import { CheckTime, hasCheckTimes } from '@/app/components/hotel-detail/HotelCheckinCheckout';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useHotelMapModal } from '@/app/components/hotel-detail/HotelMapModalProvider';
import { useHotelStayDates } from '@/app/components/hotel-detail/hooks/useHotelStayDates';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useMediaSSR } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { trackHotelReviewsLink, trackViewMap } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { createDetailPageUserActionPayload } from '@/app/common/starTrack/events/detailPageUserAction.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import { URLParameters } from '@/app/common/urlParameters';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import { useIsModalViewport } from '@/app/components/search/utils/useSsrMedia';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { EvoHotelDetailsModalContent } from './EvoHotelDetailsModalContent';
import { EvoHotelDetailsHeader, MODAL_SECTION_IDS } from './EvoHotelDetailsHeader';

const Container = styled.div`
  grid-area: hotel-info;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 16px 0;
  width: 100%;
  box-sizing: border-box;

  @media ${customCss.breakpoints.sm} {
    padding: 16px 24px 0;
  }

  @media ${customCss.breakpoints.lg} {
    flex: 1 1 50%;
    min-width: min-content;
    padding: 16px;
  }
`;

const Title = styled.h1`
  margin: 0;
  display: inline;
  ${({ theme }) => theme.kallisto.mixins.font('Title2')};

  @media ${customCss.breakpoints.lg} {
    /* trailing margin only shows after the last word — gives a 4px gap before the
       inline stars on the same line, but collapses (no indent) when they wrap below */
    margin-right: 4px;
    ${({ theme }) => theme.kallisto.mixins.font('Title3')};
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media ${customCss.breakpoints.lg} {
    display: block;
  }
`;

const HotelInfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  @media ${customCss.breakpoints.lg} {
    gap: 8px;
  }
`;

const CheckinBlock = styled.div`
  /* desktop-only: first paint may render this below lg (UA-based isMobile default),
     so it must stay hidden there to avoid a layout jump on hydration */
  display: none;
  flex-direction: column;
  gap: 2px;

  @media ${customCss.breakpoints.lg} {
    display: flex;
  }
`;

/* The labels, the dates and the times are three independent rows rather than two stacked
   columns: a long time label (e.g. "hasta las 12:00 h") no longer widens the check-in/out
   sides, so the dashed connector keeps spanning the gap between the two dates. */
const CheckinBlockRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.kallisto.space.space100};
`;

/* Wider gap than the other two rows: 16px sets each date off from its dashed line, while the
   nights label keeps the tighter space100 on both sides (see NightsContainer). */
const DatesRow = styled(CheckinBlockRow)`
  gap: ${({ theme }) => theme.kallisto.space.space200};
`;

const CheckinLine = styled.div`
  flex: 1;
  min-width: 8px;
  border-top: 1px dashed ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
`;

const NightsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.kallisto.space.space100};
  align-items: center;
  flex-grow: 1;
`;

/* The design leaves 24px above the links and 16px around the divider: 8px of vertical padding
   here plus the 16px Container gap makes up the 24px. Below the links it only reaches 24px at lg,
   where Container has a bottom padding — below lg Container ends flush, so the 8px is all there is. */
const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space200};
  padding: ${({ theme }) => theme.kallisto.space.space100} 0;

  & > * {
    flex: 1;
  }
`;

const VerticalSeparator = styled.div`
  width: 1px;
  height: 24px;
  align-self: center;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  flex: none;
`;

const HotelInfoAndCheckinContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DateLabel = styled(Text).attrs({ variant: 'Body', weight: 'bold' })`
  line-height: 22px;
  white-space: nowrap;
`;

/* Half of the row each, so the times sit under their own date and the loading placeholder
   (sized in %) has a definite width to resolve against. Deliberately no `min-width: 0`: the
   times are nowrap, so the content-based minimum is what stops a long one (hu "az alabbi
   idopontig: 12:00") from being squeezed under its text width and overprinting its neighbour.
   The row's space-between and gap then hold the two apart and the panel takes the width it
   needs, which `min-width: min-content` on Container already allows. */
const CheckTimeText = styled.div`
  flex: 1;

  span {
    ${({ theme }) => theme.kallisto.mixins.font('Body')};
  }
`;

export function EvoHeroInfoPanel() {
  const { t } = useTranslation();
  const { data: staticData } = useFetchHotelStatic();
  const isStaticDataLoading = useIsHotelStaticLoading();
  const { localization, staticData: pageStaticData, commonData } = usePageWrapperData();
  const { reviewsDisabled } = localization.configValuesApp;
  const isTripAdvisor = isTripAdvisorSource(staticData?.hotelOpinion.reviewSource);
  // Only fetch the TrustYou meta-review for an explicit TrustYou hotel — not for TA, unknown,
  // absent or Expedia sources (which show no reviews at all).
  const isTrustYou = isTrustYouSource(staticData?.hotelOpinion.reviewSource);
  const { metaReview } = useMetaReview(
    isTrustYou && !reviewsDisabled ? staticData?.hotelOpinion.opinionId : undefined,
    localization.lang
  );
  const { sendCuriosityClickEvent } = useCuriosity();
  const { showMap } = useHotelMapModal();
  const { starTrack } = useCommonClient();
  // undefined until the viewport is measured after hydration; until then the layout
  // falls back to the UA-based guess, which is wrong on tablets with desktop UAs
  const isLgMeasured = useMediaSSR(customCss.breakpoints.lg);
  const isMobile = !(isLgMeasured ?? commonData.isDesktopDevice);
  // Tabs, drawer-vs-dialog, fullHeight and content keep the lg gate above (MPH-839). The sheet gesture and
  // its mobile classes instead follow the design system's own sheet/dialog switch (MODAL_DESKTOP_BREAKPOINT_PX),
  // so the centred tablet dialog no longer keeps swipe-to-dismiss (MPH-748) — the same split as RoomPanelModal.
  const isSheet = useIsModalViewport(commonData.isMobileDevice);
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const { productDetailKey } = useInitialParams();
  const { checkin, checkout, nights } = useHotelStayDates();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(MODAL_SECTION_IDS.details);

  const openModal = () => {
    // Always reopen on the first tab so the modal never lands on a stale section. Mobile and
    // desktop expose different tab sets, so their first tab differs.
    setActiveSection(isMobile ? MODAL_SECTION_IDS.overview : MODAL_SECTION_IDS.details);
    setIsModalOpen(true);
  };

  // The reviews summary opens the same hotel-details modal, but jumps straight to its Reviews tab.
  const openReviewsModal = useCallback(() => {
    setActiveSection(MODAL_SECTION_IDS.reviews);
    setIsModalOpen(true);
  }, []);

  // Mobile and desktop expose different tab sets: the first tab is `overview` on mobile and
  // `details` on desktop, and facilities/location are mobile-only. If the viewport crosses the lg
  // breakpoint while the modal is open, remap a now-invalid section to the current breakpoint's
  // first tab so the modal never lands on a section that no longer exists (which would otherwise
  // render blank with no active tab). Reviews/policies exist on both, so they're left untouched.
  useEffect(() => {
    setActiveSection((current) => {
      if (isMobile && current === MODAL_SECTION_IDS.details) return MODAL_SECTION_IDS.overview;
      if (
        !isMobile &&
        (current === MODAL_SECTION_IDS.overview ||
          current === MODAL_SECTION_IDS.facilities ||
          current === MODAL_SECTION_IDS.location)
      ) {
        return MODAL_SECTION_IDS.details;
      }
      return current;
    });
  }, [isMobile]);

  const reviewScore = isTripAdvisor
    ? staticData?.hotelOpinion.rating
    : metaReview?.score || staticData?.hotelOpinion.rating;
  const reviewCount = isTripAdvisor
    ? staticData?.hotelOpinion.numberOfReviews
    : metaReview?.reviewCount || staticData?.hotelOpinion.numberOfReviews;

  const { checkin: checkinLabel, checkout: checkoutLabel } = getCheckinCheckoutDates(
    localization.locale,
    checkin,
    checkout,
    URL_DATE_FORMAT,
    { weekday: 'short', day: 'numeric', month: 'short' }
  );

  const onReviewsClick = () => {
    starTrack?.detailPageUserAction(
      createDetailPageUserActionPayload({
        searchId: searchId || '',
        productDetailKey,
        action: 'REVIEW',
      })
    );
    sendCuriosityClickEvent({
      event_name: isMobile ? 'click_reviews_link' : 'click_reviews_show_all',
      event_context: 'review_summary',
    });
    trackHotelReviewsLink();
  };

  const openMap = () => {
    showMap();
    trackViewMap();
    sendCuriosityClickEvent({
      event_name: 'click_view_map_from_hotel_info',
      event_context: 'from_hotel_info',
    });
  };

  if (!staticData) return null;

  // The dates row above already carries the stay, so a hotel with no published times just loses
  // this row rather than keeping two placeholder bars that never resolve (MPH-1007).
  const showCheckTimes =
    isStaticDataLoading ||
    hasCheckTimes(staticData.checkinInformation) ||
    hasCheckTimes(staticData.checkoutInformation);

  return (
    <Container>
      <HotelInfoAndCheckinContainer>
        <HotelInfoGroup>
          <TitleBlock>
            <Title>{staticData.name}</Title>
            {staticData.rating > 0 && (
              <Stars
                stars={staticData.rating}
                black={true}
                css={`
                  display: inline-flex;

                  @media ${customCss.breakpoints.lg} {
                    vertical-align: text-bottom;
                    margin-bottom: 2px;
                  }
                `}
              />
            )}
          </TitleBlock>
          {!reviewsDisabled && (
            <ReviewsSummaryEvo
              reviewsScore={reviewScore}
              reviews={reviewCount}
              reviewSource={staticData?.hotelOpinion.reviewSource}
              onClick={onReviewsClick}
              // A TrustYou hotel without its opinionId shows the score but has no reviews content
              // behind it — render the summary as static text instead of a link.
              onOpenReviews={hasReviewsContent(staticData?.hotelOpinion) ? openReviewsModal : undefined}
            />
          )}
          <DebugReviewsInfo />
        </HotelInfoGroup>
        {!isMobile && (
          <CheckinBlock>
            <CheckinBlockRow>
              <Text variant="BodySmall" color="textSecondary">
                {t('vacation.common.checkin')}
              </Text>
              <Text variant="BodySmall" color="textSecondary">
                {t('vacation.common.checkout')}
              </Text>
            </CheckinBlockRow>
            <DatesRow>
              <DateLabel>{checkinLabel}</DateLabel>
              <NightsContainer>
                <CheckinLine />
                <Text
                  variant="BodySmall"
                  color="textSecondary"
                  css={`
                    white-space: nowrap;
                  `}
                >
                  {nights === 1
                    ? t('vacation.select.cc.hotel.nights.single')
                    : t('vacation.select.cc.hotel.nights', { count: nights.toString() })}
                </Text>
                <CheckinLine />
              </NightsContainer>
              <DateLabel>{checkoutLabel}</DateLabel>
            </DatesRow>
            {showCheckTimes && (
              <CheckinBlockRow>
                <CheckTimeText>
                  <CheckTime
                    from={staticData.checkinInformation?.from}
                    to={staticData.checkinInformation?.to}
                    isLoading={isStaticDataLoading}
                  />
                </CheckTimeText>
                <CheckTimeText
                  css={`
                    text-align: right;
                  `}
                >
                  <CheckTime
                    from={staticData.checkoutInformation?.from}
                    to={staticData.checkoutInformation?.to}
                    isLoading={isStaticDataLoading}
                  />
                </CheckTimeText>
              </CheckinBlockRow>
            )}
          </CheckinBlock>
        )}
      </HotelInfoAndCheckinContainer>
      <ButtonRow>
        <LinkButton variant="accent" appearance="underlined" data-testid="hotel-details-btn" onClick={openModal}>
          {t('vacation.select.cc.hotel.details_cta')}
        </LinkButton>
        <VerticalSeparator />
        <LinkButton variant="accent" appearance="underlined" data-testid="hotel-map-btn" onClick={openMap}>
          {t('vacation.select.cc.hotel.view_map_cta')}
        </LinkButton>
      </ButtonRow>
      <OpenXModal
        isOpen={isModalOpen}
        isMobile={isSheet}
        brand={pageStaticData.domainValues?.brand as Brand}
        onClose={() => setIsModalOpen(false)}
        onCloseByBrowserHistory={() => setIsModalOpen(false)}
        onOpenByBrowserHistory={() => setIsModalOpen(true)}
        fullScreen={!isMobile}
        // On mobile the bottom sheet would otherwise size to its content (max-height), so its
        // height jumps every time the active tab swaps to a shorter/taller section. fullHeight
        // pins it to a fixed height (calc(100% - space400)) so the sheet stays put across tabs.
        // Ignored on desktop, where fullScreen already forces full height.
        fullHeight={isMobile}
        className={isMobile ? 'hotel-details-modal modal-XXL' : 'hotel-details-modal hotel-modal-lateral-evo'}
        id="hotel-details-modal"
        aria-label={t('vacation.select.evo_hotel_details.modal_label') || 'Hotel details'}
        bodyBackgroundColor="backgroundPage"
        // scrollShadows drives the sticky header's drop shadow via OverlayContext: the modal
        // renders a probe inside the scroll body and exposes headerShadow (scrollTop > 0), which
        // EvoHotelDetailsHeader consumes. Keep bodyPadding at the pre-scrollShadows defaults (3 / 2)
        // so enabling it doesn't switch the content padding to EVO_BODY_PADDING.
        scrollShadows={true}
        bodyPadding={0}
        bodyPaddingS={0}
        header={{
          sticky: true,
          element: (
            <EvoHotelDetailsHeader
              handleClose={() => setIsModalOpen(false)}
              activeSection={activeSection}
              onSelectSection={setActiveSection}
              isMobile={isMobile}
            />
          ),
        }}
      >
        <EvoHotelDetailsModalContent
          activeSection={activeSection}
          isMobile={isMobile}
          onSelectSection={setActiveSection}
        />
      </OpenXModal>
    </Container>
  );
}

export function EvoHeroInfoPanelSkeleton() {
  return (
    <Container
      css={`
        gap: 8px;
      `}
    >
      <Placeholder height="24px" width="80%" />
      <Placeholder height="14px" width="80px" />
      <Placeholder height="14px" width="60%" />
      {/* Same row as the loaded state, so the placeholders stand in for two 24px text links
          rather than the ghost buttons this used to mock. */}
      <ButtonRow>
        <Placeholder height="24px" width="100%" />
        <VerticalSeparator />
        <Placeholder height="24px" width="100%" />
      </ButtonRow>
    </Container>
  );
}
